/**
 * ImportWizard — catalog transfer-bundle import (PIM Wave P5 Task 8). A
 * `BottomSheet` with three steps, driven by local state rather than an
 * explicit step enum (the step is DERIVED from what's been produced so far):
 *
 *   1. upload  — pick a `.json` file, parsed client-side into a bare
 *      `unknown` (no client-side shape validation — the server's
 *      `extra="forbid"` Pydantic models are the single source of truth for
 *      what a valid bundle looks like).
 *   2. diff    — `POST .../import/dry-run` renders the 7-entity-kind diff as
 *      a flattened `AdminDataTable` (conflict/create/update rows always
 *      shown; `skip` rows — often the bulk of an unchanged bundle — hidden
 *      behind a "show unchanged" toggle so a large, mostly-synced bundle
 *      doesn't flood the table by default). ANY conflict disables Apply.
 *   3. success — `POST .../import/apply` created a draft changeset; a link
 *      hands off to `/admin/changesets` (Task 7's page) for review+publish.
 *
 * Defensive 404 handling: the transfer-apply endpoint's own docstring says
 * it does NOT route through the `pim.changesets` feature-flag gate (transfer
 * always produces a draft changeset, never a direct write) — tracing
 * `create_draft_with_ops` confirms it never calls `changesets_enabled()`
 * either, so `pim.changeset.disabled` is not actually reachable from this
 * endpoint today. Handled anyway (brief-mandated) as a defensive branch, in
 * case that decision changes later.
 */
import { useEffect, useMemo, useState, type ChangeEvent, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { BottomSheet, Button, Field, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminDataTable, type DataTableColumn } from "@/features/admin/_shared";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import {
  isTransferChangesetsDisabledError,
  parseImportConflictDiff,
  TRANSFER_DIFF_KINDS,
  type ImportApplyResponse,
  type TransferDiff,
  type TransferDiffKind,
} from "@/features/admin/transfer/api";
import { useApplyTransferImport, useDryRunTransferImport } from "@/features/admin/transfer/hooks";

export interface ImportWizardProps {
  open: boolean;
  onClose: () => void;
}

type DiffBucketKey = "conflict" | "create" | "update" | "skip";

interface DiffRow {
  id: string;
  kind: TransferDiffKind;
  bucket: DiffBucketKey;
  slug: string;
  reason: string;
}

const BUCKET_TONE: Record<DiffBucketKey, "success" | "warn" | "neutral" | "error"> = {
  create: "success",
  update: "warn",
  skip: "neutral",
  conflict: "error",
};

function flattenDiff(diff: TransferDiff, includeSkip: boolean): DiffRow[] {
  const buckets: DiffBucketKey[] = includeSkip
    ? ["conflict", "create", "update", "skip"]
    : ["conflict", "create", "update"];
  const rows: DiffRow[] = [];
  for (const kind of TRANSFER_DIFF_KINDS) {
    const bucketSet = diff[kind];
    for (const bucket of buckets) {
      for (const entry of bucketSet[bucket]) {
        rows.push({ id: `${kind}-${bucket}-${entry.slug}`, kind, bucket, slug: entry.slug, reason: entry.reason });
      }
    }
  }
  return rows;
}

export function ImportWizard({ open, onClose }: ImportWizardProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedBundle, setParsedBundle] = useState<unknown | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [diff, setDiff] = useState<TransferDiff | null>(null);
  const [includeSkip, setIncludeSkip] = useState(false);
  const [titleInput, setTitleInput] = useState("");
  const [applied, setApplied] = useState<ImportApplyResponse | null>(null);

  const dryRunMutation = useDryRunTransferImport();
  const applyMutation = useApplyTransferImport();

  useEffect(() => {
    if (open) return;
    setParseError(null);
    setParsedBundle(null);
    setFileName(null);
    setDiff(null);
    setIncludeSkip(false);
    setTitleInput("");
    setApplied(null);
  }, [open]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const picked = event.target.files?.[0] ?? null;
    setParseError(null);
    setParsedBundle(null);
    setFileName(picked?.name ?? null);
    setDiff(null);
    if (!picked) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = typeof reader.result === "string" ? reader.result : "";
        setParsedBundle(JSON.parse(text) as unknown);
      } catch {
        setParseError(t("pim.transfer.wizard.invalidJson"));
      }
    };
    reader.onerror = () => setParseError(t("pim.transfer.wizard.invalidJson"));
    reader.readAsText(picked);
  };

  const handlePreview = (): void => {
    if (parsedBundle === null) return;
    dryRunMutation.mutate(parsedBundle, {
      onSuccess: (result) => setDiff(result),
      onError: () => toast.error(t("error.generic")),
    });
  };

  const handleApply = (): void => {
    if (parsedBundle === null) return;
    applyMutation.mutate(
      { bundle: parsedBundle, title: titleInput.trim() === "" ? undefined : titleInput.trim() },
      {
        onSuccess: (result) => {
          setApplied(result);
          toast.success(t("pim.transfer.wizard.applySuccess"));
        },
        onError: (err) => {
          if (isTransferChangesetsDisabledError(err)) {
            toast.error(t("pim.transfer.wizard.disabledFallback"));
            return;
          }
          const conflictDiff = parseImportConflictDiff(err);
          if (conflictDiff) {
            setDiff(conflictDiff);
            toast.error(t("pim.transfer.wizard.applyConflict"));
            return;
          }
          toast.error(t("error.generic"));
        },
      },
    );
  };

  const rows = useMemo(() => (diff ? flattenDiff(diff, includeSkip) : []), [diff, includeSkip]);

  const columns: DataTableColumn[] = [
    { key: "kind", label: t("pim.transfer.wizard.columns.kind") },
    { key: "bucket", label: t("pim.transfer.wizard.columns.bucket") },
    { key: "slug", label: t("pim.transfer.wizard.columns.slug") },
    { key: "reason", label: t("pim.transfer.wizard.columns.reason") },
  ];

  const renderCell = (row: DiffRow, key: string): ReactNode => {
    switch (key) {
      case "kind":
        return t(`pim.transfer.wizard.kind.${row.kind}`);
      case "bucket":
        return (
          <GPill tone={BUCKET_TONE[row.bucket]} size="sm">
            {t(`pim.transfer.wizard.bucket.${row.bucket}`)}
          </GPill>
        );
      case "slug":
        return <span className="font-mono text-xs">{row.slug}</span>;
      case "reason":
        return row.reason;
      default:
        return "";
    }
  };

  const renderMobileCard = (row: DiffRow): ReactElement => (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-[var(--g-secondary)]">
          {t(`pim.transfer.wizard.kind.${row.kind}`)}
        </span>
        <GPill tone={BUCKET_TONE[row.bucket]} size="sm">
          {t(`pim.transfer.wizard.bucket.${row.bucket}`)}
        </GPill>
      </div>
      <span className="font-mono text-xs">{row.slug}</span>
      <span className="text-sm text-[var(--g-secondary)]">{row.reason}</span>
    </div>
  );

  const renderUploadStep = (): ReactElement => (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[var(--g-secondary)]">{t("pim.transfer.wizard.uploadHint")}</p>
      <input
        type="file"
        accept=".json"
        data-testid="pim-transfer-file-input"
        aria-label={t("pim.transfer.wizard.chooseFile")}
        onChange={handleFileChange}
        className="text-sm"
      />
      {fileName ? (
        <p className="text-xs text-[var(--g-secondary)]">{fileName}</p>
      ) : null}
      {parseError ? (
        <p role="alert" className="text-sm text-[var(--g-error)]">
          {parseError}
        </p>
      ) : null}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
          {t("common.cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          data-testid="pim-transfer-preview-btn"
          disabled={parsedBundle === null}
          loading={dryRunMutation.isPending}
          onClick={handlePreview}
          className="min-h-[44px]"
        >
          {t("pim.transfer.wizard.previewButton")}
        </Button>
      </div>
    </div>
  );

  const renderDiffStep = (currentDiff: TransferDiff): ReactElement => (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <GPill tone="error" size="sm">
          {t("pim.transfer.wizard.totals.conflict", { count: currentDiff.totals.conflict })}
        </GPill>
        <GPill tone="success" size="sm">
          {t("pim.transfer.wizard.totals.create", { count: currentDiff.totals.create })}
        </GPill>
        <GPill tone="warn" size="sm">
          {t("pim.transfer.wizard.totals.update", { count: currentDiff.totals.update })}
        </GPill>
        <GPill
          tone="neutral"
          size="sm"
          onClick={() => setIncludeSkip((v) => !v)}
          active={includeSkip}
        >
          {t("pim.transfer.wizard.totals.skip", { count: currentDiff.totals.skip })}
        </GPill>
      </div>

      {currentDiff.totals.conflict > 0 ? (
        <p role="alert" className="text-sm text-[var(--g-error)]">
          {t("pim.transfer.wizard.conflictWarning", { count: currentDiff.totals.conflict })}
        </p>
      ) : null}

      <AdminDataTable<DiffRow>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        rowAriaLabel={(row) => `${row.kind} ${row.slug}`}
        testid="pim-transfer-diff"
        isLoading={false}
        isError={false}
        emptyLabel={t("pim.transfer.wizard.diffEmpty")}
        errorLabel=""
      />

      <Field
        label={t("pim.transfer.wizard.titleLabel")}
        data-testid="pim-transfer-changeset-title"
        value={titleInput}
        onChange={(e) => setTitleInput(e.target.value)}
      />

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" onClick={() => setDiff(null)} className="min-h-[44px]">
          {t("pim.transfer.wizard.backButton")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          data-testid="pim-transfer-apply-btn"
          disabled={currentDiff.totals.conflict > 0}
          loading={applyMutation.isPending}
          onClick={handleApply}
          className="min-h-[44px]"
        >
          {t("pim.transfer.wizard.applyButton")}
        </Button>
      </div>
    </div>
  );

  const renderSuccessStep = (result: ImportApplyResponse): ReactElement => (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[var(--g-secondary)]">{t("pim.transfer.wizard.applySuccess")}</p>
      <p
        data-testid="pim-transfer-changeset-id"
        className="font-mono text-xs text-[var(--g-secondary)]"
      >
        {t("pim.transfer.wizard.applySuccessId", { id: result.changeset_id.slice(0, 8) })}
      </p>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
          {t("pim.transfer.wizard.closeButton")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          data-testid="pim-transfer-goto-changesets"
          onClick={() => {
            onClose();
            navigate("/admin/changesets");
          }}
          className="min-h-[44px]"
        >
          {t("pim.transfer.wizard.goToChangesets")}
        </Button>
      </div>
    </div>
  );

  return (
    <BottomSheet open={open} onClose={onClose} title={t("pim.transfer.wizard.title")} desktop={isDesktop}>
      {applied ? renderSuccessStep(applied) : diff ? renderDiffStep(diff) : renderUploadStep()}
    </BottomSheet>
  );
}

export default ImportWizard;
